import { ComponentProps, ReactNode } from 'react';

import Logo from '@v2/shared/Layouts/Full/Navigation/Logo';

import Navigation from './Navigation';
import SaleShell, { MegaSaleLayoutProps } from './SaleShell';

export type SharedMegaSaleLayoutProps = MegaSaleLayoutProps & {
  /**
   * The brand's mark, for the header.
   *
   * The one thing this layout cannot answer for itself: a mark is a file in the brand's
   * own `Layouts/Full/Navigation/images`, and nothing under `shared/` may reach into a
   * brand folder to pick one. A brand supplies it from its own `Layouts/MegaSale`, which
   * is a dozen lines and exists for this and the footer.
   */
  logo?: ComponentProps<typeof Logo>;
  /** The brand's footer — its own `Layouts/Full/Footer`, for the same reason. */
  footer?: ReactNode;
};

/**
 * The sale layout MagSpot, MagZoom and TopMags share.
 *
 * `SaleShell` — the providers, the cart drawer and the floor under the content — with
 * the two-strip sale header on top of it. What a brand adds is its mark and its footer,
 * and that is the whole of its own `Layouts/MegaSale`.
 *
 * DiscountMags does not resolve to this. Its sale header is Full's fixed header with the
 * categories taken out, which is a different arrangement rather than a recolouring — it
 * measures its own height, carries an account slide-over and a support popover, and the
 * page's sticky headings clear it by a custom property this layout has no use for. So it
 * keeps `brands/discountmags/Layouts/MegaSale`, which composes the same `SaleShell`.
 *
 * Resolvable as `Layouts.MegaSale` on its own, without a brand file, so a brand that has
 * not been given a mark yet renders the sale rather than throwing — which is the failure
 * this whole change exists to end. It comes up one mark short, not one page short.
 */
export default function MegaSale({ logo, footer, children, ...shellProps }: SharedMegaSaleLayoutProps) {
  return (
    <SaleShell
      {...shellProps}
      navigation={(searchProps) => <Navigation searchProps={searchProps} logo={logo} />}
      footer={footer}
    >
      {children}
    </SaleShell>
  );
}
